import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plane, Plus, Search, Eye, MapPin, Calendar } from 'lucide-react';
import { fmtDate, fmtCurrency } from '@/lib/dates';
import SPPDMap from '@/components/SPPDMap';

export default function SPPDListPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', jenis: 'all', search: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.jenis !== 'all') params.set('jenis', filters.jenis);
      if (filters.search) params.set('search', filters.search);
      const { data } = await api.get(`/sppd?${params.toString()}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.jenis, filters.search]);

  const aktif = items.filter((s) => s.status === 'Sedang Dinas');
  const canCreate = user?.role === 'admin' || user?.role === 'operator';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Monitoring SPPD"
        subtitle="Pantau perjalanan dinas pejabat Pemkot Makassar secara real-time."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'SPPD' }]}
        action={canCreate && (
          <Link to="/sppd/baru"><Button data-testid="add-sppd-button" className="bg-[#DC2626] hover:bg-[#B91C1C]"><Plus className="w-4 h-4 mr-2" />Registrasi SPPD</Button></Link>
        )}
      />

      {/* Map */}
      <Card className="border-slate-200 shadow-premium">
        <CardHeader>
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#DC2626]" /> Pejabat Sedang Dinas
            <span className="ml-auto text-xs font-medium text-slate-500">{aktif.length} aktif</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SPPDMap items={aktif} height={320} />
        </CardContent>
      </Card>

      {/* Filter */}
      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="sppd-search-input" placeholder="Cari pejabat, tujuan, nomor SPPD…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9" />
          </div>
          <Select value={filters.jenis} onValueChange={(v) => setFilters({ ...filters, jenis: v })}>
            <SelectTrigger className="w-full md:w-44" data-testid="sppd-filter-jenis"><SelectValue placeholder="Jenis" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Jenis</SelectItem>
              <SelectItem value="Dalam Negeri">Dalam Negeri</SelectItem>
              <SelectItem value="Luar Negeri">Luar Negeri</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-full md:w-56" data-testid="sppd-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved (Belum Berangkat)</SelectItem>
              <SelectItem value="Sedang Dinas">Sedang Dinas</SelectItem>
              <SelectItem value="Selesai">Selesai</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-premium overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>
        ) : items.length === 0 ? (
          <EmptyState title="Belum ada SPPD" description="Tidak ada perjalanan dinas pada filter ini." />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="sppd-data-table">
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead>Nomor SPPD</TableHead>
                  <TableHead>Pejabat</TableHead>
                  <TableHead className="hidden md:table-cell">Tujuan</TableHead>
                  <TableHead className="hidden lg:table-cell">Periode</TableHead>
                  <TableHead className="hidden xl:table-cell">Biaya</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right w-16">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-xs text-slate-700">{s.nomor_sppd}</TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-800">{s.pejabat_nama}</div>
                      <div className="text-xs text-slate-500">{s.jabatan}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {s.jenis_perjalanan === 'Luar Negeri' ? (
                        <>
                          <div className="font-medium text-slate-800 flex items-center gap-1.5">
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-700">LN</span>
                            {s.kota_luar_negeri || '-'}
                          </div>
                          <div className="text-xs text-slate-500">{s.negara_tujuan}</div>
                        </>
                      ) : (
                        <>
                          <div className="font-medium text-slate-800 flex items-center gap-1.5">
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">DN</span>
                            {s.tujuan_kota}
                          </div>
                          <div className="text-xs text-slate-500">{s.tujuan_provinsi}</div>
                        </>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs">
                      <div>{fmtDate(s.tanggal_berangkat)}</div>
                      <div className="text-slate-500">s.d. {fmtDate(s.tanggal_kembali)}</div>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell font-mono text-xs">{fmtCurrency(s.biaya_estimasi)}</TableCell>
                    <TableCell><StatusBadge status={s.status} type="sppd" /></TableCell>
                    <TableCell className="text-right">
                      <Link to={`/sppd/${s.id}`}><Button size="icon" variant="ghost" data-testid={`view-sppd-${s.id}`}><Eye className="w-4 h-4" /></Button></Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
